<script setup>
import { computed, ref, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMenu } from "../../composables/useMenu.js";
import { hasPermission, hasAnyPermission, COLLABORATOR_PERMISSIONS } from "../../composables/usePermissions.js";
import { useDocumentDetails } from "../../composables/useDocumentDetails.js";
import { useShareModal } from "../../composables/useShareModal.js";
import { downloadBase64File } from "../../composables/useFileDownload.js";
import ProjectService from "../../services/projectService.js";
import RenameModal from "./RenameModal.vue";
import ConfirmModal from "./ConfirmModal.vue";
import CopyToProjectModal from "./CopyToProjectModal.vue";
import DownloadOnlyModal from "./DownloadOnlyModal.vue";

const props = defineProps({
  // { id, name, origin, originMimeType }
  document: { type: Object, required: true },
  // Documents inherit the parent project's role/permissions wholesale — there
  // is no separate per-document ACL in this app.
  project: { type: Object, required: true },
  // Callers showing docs from mixed sources (e.g. Home's Recent/Starred,
  // which lists docs from both singleton and real multi-doc projects) can
  // set this to false to hide Share for non-singleton docs — sharing a real
  // project from a condensed doc row is confusing; that belongs on the
  // Project page. Docs.vue/Project.vue don't pass this, so it defaults open.
  allowShare: { type: Boolean, default: true },
  // Home.vue's Recent/Starred tabs only — these are list-membership actions
  // specific to those tabs, not applicable when this menu is shown from a
  // project's own document grid.
  allowRemoveFromRecent: { type: Boolean, default: false },
  // Shows a Star/Unstar toggle, labeled from the current document.isFavorite
  // — the caller's doc query needs to have actually fetched that field.
  allowFavoriteToggle: { type: Boolean, default: false },
});
const emit = defineEmits(["renamed", "deleted", "removed-from-recent", "starred", "unstarred", "rejected"]);

const route = useRoute();
const router = useRouter();
const $auth0 = inject("$auth0");
const projectService = new ProjectService();
const { isOpen, triggerEl, menuEl, menuStyle, toggle, close, onMenuKeydown } = useMenu();
const { open: openDetails } = useDocumentDetails();
const { open: openShare } = useShareModal();

// Download-only files (isAnnotatable: false) have nothing to show in the
// normal Details panel — there's no preview/webViewLink for them — so "View
// Details" shows the same download popup used when opening the card itself.
const showDownloadOnly = ref(false);
function onViewDetails() {
  close();
  if (props.document.isAnnotatable === false) {
    showDownloadOnly.value = true;
    return;
  }
  openDetails({ documentId: props.document.id, projectId: props.project.id, document: props.document, project: props.project });
}

const permissions = computed(() => props.project.permissions);
// Sharing is project-level (see shareService.js) — for a singleton project
// (a standalone upload's auto-created wrapper) this shares that document on
// its own, same as sharing any other single-document project. Download-only
// files (isAnnotatable: false) are excluded — there's nothing collaborative
// to share on a file that can't be viewed or annotated in the app.
const canShare = computed(() =>
  props.allowShare
  && hasPermission(permissions.value, COLLABORATOR_PERMISSIONS.SHARE_PROJECT)
);
// Copying is deliberately blocked for Contributor/Viewer, same rule as project duplication.
const canCopy = computed(() =>
  !["CONTRIBUTOR", "VIEWER"].includes(props.project.userRole)
  && hasPermission(permissions.value, COLLABORATOR_PERMISSIONS.COPY_DOCUMENT)
);

function onShare() {
  close();
  // Sharing is project-level: a singleton project has no real name of its own
  // (an unnamed one-document wrapper), so show the document's name instead;
  // a real multi-doc project has a proper name, so use that, not this document's.
  const resourceName = props.project.isSingleton ? props.document.name : props.project.name;
  openShare({ resourceId: props.project.id, resourceName });
}
// A "flattened"/annotated download only makes sense for a document that can
// actually carry annotations — download-only files (isAnnotatable: false)
// just get the plain "Download" option below.
const canDownloadAnnotated = computed(() =>
  props.document.isAnnotatable !== false
  && hasPermission(permissions.value, COLLABORATOR_PERMISSIONS.DOWNLOAD_ANNOTATED_DOCUMENT)
);
const canDownloadOriginal = computed(() =>
  hasAnyPermission(permissions.value, [COLLABORATOR_PERMISSIONS.DOWNLOAD_SOURCE_DOCUMENT, COLLABORATOR_PERMISSIONS.DOWNLOAD_ORIGINAL_DOCUMENT])
);
// Renaming is deliberately blocked for Contributor too, same rule as
// project duplication/copy above — despite RENAME_DOCUMENT otherwise being
// in Contributor's permission set.
const canRename = computed(() =>
  props.project.userRole !== "CONTRIBUTOR"
  && hasPermission(permissions.value, COLLABORATOR_PERMISSIONS.RENAME_DOCUMENT)
);
// For a singleton project (a standalone upload's auto-created single-document
// wrapper), deleting the one document IS deleting the whole project — so it
// has to be OWNER-only like any other project deletion, even though
// DELETE_DOCUMENT is otherwise granted to EDITOR too for real multi-doc projects.
const canDelete = computed(() =>
  (!props.project.isSingleton || props.project.userRole === "OWNER")
  && hasPermission(permissions.value, COLLABORATOR_PERMISSIONS.DELETE_DOCUMENT)
);
// A singleton doc has no real project to browse — ProjectCardMenu's "Reject
// Share" only ever applies to real projects, so a non-owner shared a
// standalone upload needs the same option here instead, same mutation.
const canReject = computed(() => props.project.isSingleton && props.project.userRole !== "OWNER");

// ─── Download ───────────────────────────────────────────────────────────────
// Tracks which download is in flight (not just whether one is) — the menu
// closes as soon as you click, so a second download can legitimately be
// started (of the other kind) while the first is still running in the
// background; only re-clicking the SAME kind while it's in flight is blocked.
const downloadingKind = ref(null); // null | "original" | "printable"
async function onDownload(original) {
  close();
  const kind = original ? "original" : "printable";
  if (downloadingKind.value === kind) return;
  downloadingKind.value = kind;
  try {
    const { content, originMimeType } = await projectService.downloadDocument({
      documentId: props.document.id,
      flatten: !original,
      original,
    });
    if (content) downloadBase64File(content, originMimeType, props.document.name ?? "document");
  } finally {
    downloadingKind.value = null;
  }
}

// ─── Copy to project ────────────────────────────────────────────────────────
const showCopy = ref(false);
function onCopyClick() { close(); showCopy.value = true; }

// ─── Rename ─────────────────────────────────────────────────────────────────
const showRename = ref(false);
const isRenaming = ref(false);
function onRenameClick() { close(); showRename.value = true; }
async function saveRename({ name }) {
  isRenaming.value = true;
  try {
    await projectService.updateDocumentName({ projectId: props.project.id, documentId: props.document.id, name });
    props.document.name = name;
    showRename.value = false;
    emit("renamed", { name });
  } finally {
    isRenaming.value = false;
  }
}

// ─── Remove from Recent / Unstar (Home's Recent/Starred tabs) ──────────────
const isRemovingFromRecent = ref(false);
async function onRemoveFromRecent() {
  close();
  if (isRemovingFromRecent.value) return;
  isRemovingFromRecent.value = true;
  try {
    await projectService.removeFromRecents({ documentId: props.document.id });
    emit("removed-from-recent");
  } finally {
    isRemovingFromRecent.value = false;
  }
}

const isTogglingFavorite = ref(false);
async function onToggleFavorite() {
  close();
  if (isTogglingFavorite.value) return;
  isTogglingFavorite.value = true;
  const next = !props.document.isFavorite;
  try {
    await projectService.MarkDocumentFavorite({
      documentId: props.document.id,
      isFavorite: next,
      email: localStorage.getItem("email"),
      tenantId: localStorage.getItem("tenantId"),
    });
    props.document.isFavorite = next;
    emit(next ? "starred" : "unstarred");
  } finally {
    isTogglingFavorite.value = false;
  }
}

// ─── Delete ─────────────────────────────────────────────────────────────────
const showDelete = ref(false);
const isDeleting = ref(false);
function onDeleteClick() { close(); showDelete.value = true; }
async function confirmDelete() {
  isDeleting.value = true;
  try {
    await projectService.deleteDocument({ documentId: props.document.id, projectId: props.project.id });
    showDelete.value = false;
    emit("deleted");
  } finally {
    isDeleting.value = false;
  }
}

// ─── Reject share (non-owners, singleton docs only) ────────────────────────
const showReject = ref(false);
const isRejecting = ref(false);
function onRejectClick() { close(); showReject.value = true; }
async function confirmReject() {
  isRejecting.value = true;
  try {
    const user = await $auth0.getUser();
    await projectService.shareProject({
      projectId: props.project.id,
      collaboratorEmail: user.profile.email,
      collaboratorRole: null,
    });
    showReject.value = false;
    emit("rejected");
    if (route.params.docId === props.document.id) router.push({ name: "v2-docs" });
  } finally {
    isRejecting.value = false;
  }
}
</script>

<template>
  <div class="relative shrink-0" @click.stop>
    <button
      ref="triggerEl"
      class="flex items-center justify-center w-7 h-7 rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      aria-label="Document options"
      @click="toggle"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuEl"
        role="menu"
        aria-label="Document options"
        :style="menuStyle"
        class="w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-[400] overflow-hidden"
        @keydown="onMenuKeydown"
      >
        <button v-if="allowRemoveFromRecent" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onRemoveFromRecent">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" /><path d="M12 7v5l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /><path d="M4 4l16 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /></svg>
          Remove from Recent
        </button>
        <button v-if="canShare" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onShare">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M14.336 9.125H16.4615C16.6494 9.125 16.8296 9.19401 16.9625 9.31685C17.0954 9.43968 17.17 9.60628 17.17 9.78V16.64C17.17 16.8137 17.0954 16.9803 16.9625 17.1032C16.8296 17.226 16.6494 17.295 16.4615 17.295H3.7085C3.52059 17.295 3.34038 17.226 3.20751 17.1032C3.07465 16.9803 3 16.8137 3 16.64V9.78C3 9.60628 3.07465 9.43968 3.20751 9.31685C3.34038 9.19401 3.52059 9.125 3.7085 9.125H5.834" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M10.082 2.29111V11.8359" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M13.4925 5.70117L10.0837 2.2923L6.6748 5.70117" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Share
        </button>
        <button v-if="allowFavoriteToggle" role="menuitem" :disabled="isTogglingFavorite" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50" @click="onToggleFavorite">
          <svg width="14" height="14" viewBox="0 0 24 24" :fill="document.isFavorite ? 'currentColor' : 'none'" :class="document.isFavorite ? 'text-yellow-500' : ''"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" /></svg>
          {{ document.isFavorite ? "Unstar" : "Star" }} Document
        </button>
        <button v-if="canCopy" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onCopyClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="8" y="8" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.8" /><path d="M4 16V6a2 2 0 0 1 2-2h10" stroke="currentColor" stroke-width="1.8" /></svg>
          Copy to Project
        </button>
        <button v-if="canRename" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onRenameClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Rename Document
        </button>
        <div v-if="canDownloadOriginal || canDownloadAnnotated" class="h-px bg-slate-100 dark:bg-slate-700 my-1.5 mx-2" />
        <button v-if="canDownloadOriginal" role="menuitem" :disabled="downloadingKind === 'original'" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50" @click="onDownload(true)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          {{ downloadingKind === 'original' ? "Downloading…" : (document.isAnnotatable === false ? "Download" : "Download original file") }}
        </button>
        <button v-if="canDownloadAnnotated" role="menuitem" :disabled="downloadingKind === 'printable'" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50" @click="onDownload(false)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          {{ downloadingKind === 'printable' ? "Downloading…" : "Download flattened PDF" }}
        </button>
        <div class="h-px bg-slate-100 dark:bg-slate-700 my-1.5 mx-2" />
        <button role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="onViewDetails">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" /><path d="M12 11v5M12 8h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /></svg>
          View Details
        </button>
        <button v-if="canReject" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="onRejectClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /><polyline points="16 17 21 12 16 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /><line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Reject Share
        </button>
        <button v-if="canDelete" role="menuitem" class="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left border-0 bg-transparent cursor-pointer transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="onDeleteClick">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          Delete Document
        </button>
      </div>
    </Teleport>

    <DownloadOnlyModal
      v-if="showDownloadOnly"
      :document="document"
      :project="project"
      @close="showDownloadOnly = false"
    />

    <CopyToProjectModal
      v-if="showCopy"
      :document-id="document.id"
      :source-project-id="project.id"
      @close="showCopy = false"
    />

    <RenameModal
      v-if="showRename"
      title="Rename Document"
      :initial-name="document.name"
      :is-saving="isRenaming"
      @close="showRename = false"
      @save="saveRename"
    />

    <ConfirmModal
      v-if="showDelete"
      title="Delete Document?"
      :message="`This permanently deletes “${document.name}” and its annotations. This can't be undone.`"
      confirm-label="Delete Document"
      busy-label="Deleting…"
      :is-busy="isDeleting"
      @close="showDelete = false"
      @confirm="confirmDelete"
    />

    <ConfirmModal
      v-if="showReject"
      title="Reject Share?"
      :message="`You'll lose access to “${document.name}” until someone shares it with you again.`"
      confirm-label="Reject Share"
      busy-label="Rejecting…"
      :is-busy="isRejecting"
      @close="showReject = false"
      @confirm="confirmReject"
    />
  </div>
</template>
